import { api, API, TENANT_ID_KEY } from "@/lib/api";

//: Media is SAME-ORIGIN on whichever host serves the page (every Hosting target rewrites `/api/**`),
//: exactly like XHR (`API` in lib/api.js). Only local dev, where the SPA and API are separate
//: origins, needs the absolute backend origin. Prefixing `REACT_APP_BACKEND_URL` in a deployed build
//: pinned images to one host: CSP-blocked / 503 on `*.firebaseapp.com`, a city's custom domain, prod.
const mediaOrigin = () =>
  process.env.NODE_ENV === "development" ? process.env.REACT_APP_BACKEND_URL || "" : "";

//: Matches a LEGACY stored `/api/media/<uuid>/<variant>` URL — the shape `cover_image_url`,
//: `logo_url` and every `image_url` were saved as before the public media route existed. The
//: authenticated route it points at needs a bearer token `<img src>` never sends, which is the
//: whole P0: a saved cover/logo/menu photo 401'd everywhere it rendered. New uploads are already
//: saved in the public form (`/api/public/media/<tenant_id>/<id>/<variant>`) by the backend
//: (`MediaOut.of`), so this rewrite only matters for URLs a city saved before that change shipped.
const LEGACY_MEDIA_RE = /^\/api\/media\/([0-9a-f-]{36})\/([a-z]+)$/i;

/** THE shared media resolver - every image the web app renders (citizen pages and the admin console)
 * goes through it. Media URLs are host-relative (`/api/public/media/...`, APP_RULES §6) and served
 * same-origin on every host, the admin host included (`AdminHostMiddleware` allows that one read),
 * so no per-host prefixing happens here - one place to change if that ever stops being true. */
export function resolveImage(url) {
  if (!url) return "";
  if (url.startsWith("/api/")) {
    const legacy = url.match(LEGACY_MEDIA_RE);
    if (legacy) {
      const tenantId = (() => {
        try {
          return localStorage.getItem(TENANT_ID_KEY) || "";
        } catch {
          // Private window / blocked storage — fall through to the untouched URL below, which
          // still 401s, exactly as it did before this rewrite existed.
          return "";
        }
      })();
      if (tenantId) {
        const [, mediaId, variant] = legacy;
        return `${mediaOrigin()}/api/public/media/${tenantId}/${mediaId}/${variant}`;
      }
      // No tenant id known yet (e.g. resolved before `loadTenant` has run) — leave the URL as
      // is rather than guess. It behaves exactly as it did before this fix.
      return `${mediaOrigin()}${url}`;
    }
    return `${mediaOrigin()}${url}`;
  }
  return url;
}

//: Widths of the three stored variants (`MEDIA_VARIANTS` in the backend's media_service.py).
const VARIANT_WIDTHS = { thumb: 240, card: 900, detail: 1600 };
const VARIANT_TAIL_RE = /\/(thumb|card|detail)$/;

/** The same stored image at another size: `.../card` -> `.../detail`. A URL that is not a
 * public-media URL (an external link, empty) comes back unchanged. */
export function mediaVariant(url, variant) {
  if (!url || !VARIANT_TAIL_RE.test(url)) return url || "";
  return url.replace(VARIANT_TAIL_RE, `/${variant}`);
}

/** `srcset` over thumb/card/detail for a stored image (same shape as `resolveImage`, so it is
 * host-correct everywhere, the admin host included). "" when the URL has no variants to offer. */
export function mediaSrcSet(url) {
  if (!url || !VARIANT_TAIL_RE.test(url)) return "";
  return Object.entries(VARIANT_WIDTHS)
    .map(([name, w]) => `${resolveImage(mediaVariant(url, name))} ${w}w`)
    .join(", ");
}

export async function uploadImage(file, businessId) {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post(`/media/upload${businessId ? `?business_id=${businessId}` : ""}`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

export { API };

/** Platform-admin upload of a city's own image (its hero) into THAT city - the same media pipeline as
 * `uploadImage`, on the admin plane's path-city route. Resolves `{ url }` (origin-relative public URL). */
export async function uploadCityMedia(tenantId, file) {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post(`/admin/tenants/${tenantId}/media`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}
